import { DOCUMENT } from "@angular/common";
import { Injectable, inject } from "@angular/core";
import { PendoPayload } from "./pendo-payload.model";
import { PENDO_CONFIG } from "./pendo.config";
import type { PendoProfile } from "./profile/pendo-profile.model";

export const PENDO_SCRIPT_RETRY_DELAY_MS = 5_000;
const PENDO_SCRIPT_ATTEMPTS = 2;

/**
 * The only code that talks to Pendo's browser agent.
 *
 * Installs Pendo's own command-queue stub before the agent script loads, so a
 * caller can identify at any moment: calls made before the script arrives are
 * buffered and replayed by it, and calls made when it never arrives (an ad
 * blocker) are harmless. Nothing ever waits on the script.
 *
 * The compiler can't check Pendo's side of this, so the e2e suite's Pendo
 * agent contract spec checks it against the live script; an agent method
 * called here for the first time belongs in that spec too, and in the unit
 * specs' model of the agent (`__tests__/pendo-vendor.fake.ts`).
 *
 * The agent replaces its own copy of the metadata on each call, so every call
 * carries the whole profile.
 */
@Injectable()
export class PendoAgent {
    private readonly _document = inject(DOCUMENT);
    private readonly _apiKey = inject(PENDO_CONFIG).apiKey;
    private readonly _pendo: PendoGlobal;
    private _initialized = false;
    private _scriptAttempts = 0;

    constructor() {
        this._pendo = installStub(this._window);
        this._loadScript();
    }

    /** Starts, or switches, whose session this is. */
    identify(profile: PendoProfile): void {
        const payload = PendoPayload.from(profile);
        if (this._initialized) {
            this._agent.identify(payload);
        } else {
            this._agent.initialize(payload);
            this._initialized = true;
        }
    }

    /** Adds what's newly known about the session already identified. */
    update(profile: PendoProfile): void {
        if (!this._initialized) {
            this.identify(profile);
            return;
        }
        this._agent.updateOptions(PendoPayload.from(profile));
    }

    /** Ends the session, so nothing further is attributed to it. */
    clear(): void {
        if (!this._initialized) return;
        this._initialized = false;
        const agent = this._agent;
        if (typeof agent.teardown === "function") {
            agent.teardown();
        } else if (agent._q) {
            // The script hasn't run yet: drop the buffered session instead.
            agent._q.length = 0;
        }
    }

    private get _window(): PendoWindow {
        return (this._document.defaultView ?? window) as PendoWindow;
    }

    /**
     * The loaded script may replace the stub object, so every call goes
     * through the global rather than a reference taken at construction.
     */
    private get _agent(): PendoGlobal {
        return this._window.pendo ?? this._pendo;
    }

    private _loadScript(): void {
        this._scriptAttempts++;
        const script = this._document.createElement("script");
        script.async = true;
        script.src = `https://cdn.pendo.io/agent/static/${encodeURIComponent(this._apiKey)}/pendo.js`;
        script.onerror = () => {
            script.remove();
            if (this._scriptAttempts < PENDO_SCRIPT_ATTEMPTS) {
                setTimeout(
                    () => this._loadScript(),
                    PENDO_SCRIPT_RETRY_DELAY_MS,
                );
            } else {
                console.warn(
                    "The Pendo agent script could not be loaded (likely blocked by the browser or an extension); product analytics are off for this page.",
                );
            }
        };
        this._document.head.appendChild(script);
    }
}

interface PendoGlobal {
    initialize(payload: PendoPayload): void;
    identify(payload: PendoPayload): void;
    updateOptions(payload: PendoPayload): void;
    teardown?(): void;
    _q?: unknown[];
}

type PendoWindow = Window & { pendo?: PendoGlobal };

const QUEUED_METHODS = [
    "initialize",
    "identify",
    "updateOptions",
    "pageLoad",
    "track",
] as const;

/** Pendo's install snippet, minus the script tag it also adds. */
function installStub(window: PendoWindow): PendoGlobal {
    const pendo = (window.pendo ??= {} as PendoGlobal);
    const queue = (pendo._q ??= []);
    for (const method of QUEUED_METHODS) {
        const stub = pendo as unknown as Record<string, unknown>;
        stub[method] ??= (...args: unknown[]) => {
            // `initialize` jumps the queue, exactly as in Pendo's snippet.
            queue[method === "initialize" ? "unshift" : "push"]([
                method,
                ...args,
            ]);
        };
    }
    return pendo;
}
